import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const governmentUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Political and Economic Changes and Development",
  examWeight: "16–24%",
  focus: "How global economic and technological forces, international organizations, and development policies shape political systems and citizen well-being.",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Impact of Global Economic and Technological Forces",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Economic Globalization</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The increasing interconnectedness of economic networks across national borders. Involves worldwide market with actors unconstrained by political borders and general reduction in state control over economies.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Key Characteristics:</strong> Deepened cross-national connections, rapid flow of goods/services/capital/people, neoliberalism as ideological driver.</p>
                <p className="text-sm text-slate-700"><strong>Technological Integration:</strong> Internet and social media allow instant communication, challenging state control over information (Great Firewall in China vs. global VPNs).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Impact on Course Countries</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Policy:</strong> Massive economic liberalization post-1978 (Deng Xiaoping)</p>
                <p className="text-sm text-slate-700"><strong>Impact:</strong> Lifted hundreds of millions out of poverty; created massive middle class</p>
                <p className="text-sm text-slate-700"><strong>Legitimacy:</strong> Majority of Chinese citizens expect children to be better off, bolstering CCP legitimacy despite lack of political freedom</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Policy:</strong> Often forced to liberalize by international organizations (IMF)</p>
                <p className="text-sm text-slate-700"><strong>Impact:</strong> GDP has grown, but wealth inequality remains extreme. Reliance on oil exports links economy directly to global price fluctuations</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Policy:</strong> Entry into NAFTA (now USMCA) integrated Mexico into North American supply chain</p>
                <p className="text-sm text-slate-700"><strong>Impact:</strong> Significant growth of middle class, particularly in industrialized North. Devastated subsistence farmers in South (Zapatista uprising)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Challenges to State Control</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-800">MNCs (Multinational Corporations)</h4>
              <p className="text-sm text-slate-700 mt-2">Large companies (e.g., Shell in Nigeria) dominate markets. Can conflict with domestic policies regarding labor rights, environmental protection, and taxation. Sometimes wield more economic power than developing states they operate in.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Political Responses to Global Market Forces",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Privatization vs. Nationalization</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Privatization</h4>
                <p className="text-sm text-slate-700 mt-2">Selling state-owned enterprises to private investors. Goal: Efficiency, competition.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Nationalization</h4>
                <p className="text-sm text-slate-700 mt-2">State takeover of private companies. Goal: Sovereignty, revenue control.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Responses by Course Country</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China (State Capitalism)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Special Economic Zones (SEZs):</strong> Designated areas (e.g., Shenzhen) where free-market mechanisms and foreign investment allowed, while rest of country maintains stricter control</p>
                <p className="text-sm text-slate-700"><strong>SOEs:</strong> State-Owned Enterprises still dominate critical sectors (banking, energy), ensuring CCP retains ultimate control</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico (Liberalization)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>PEMEX:</strong> For decades, state-owned oil company was symbol of sovereignty. Recent reforms allowed private/foreign investment into energy sector to boost efficiency</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇳🇬 Nigeria (Joint Ventures)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>NNPC:</strong> State-owned but lacks technology to extract deep-water oil. Forms joint ventures with foreign firms (Shell, Chevron), leading to tensions over profit-sharing and environmental damage</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia (Re-Nationalization)</h4>
                <p className="text-sm text-slate-700 mt-2">Under Putin, state re-asserted control over strategic industries, specifically oil and gas (Gazprom and Rosneft). Oligarchs who opposed Putin were jailed or exiled; assets seized by state</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2">Historically liberalized under Thatcher (privatized rail, telecom). Currently allows most private control of natural resources among six countries</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Challenges from Globalization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Threats to Sovereignty</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Foreign Direct Investment (FDI)</h4>
                <p className="text-sm text-slate-700 mt-2">Investment from abroad can bring demands for legal changes or political alignment</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Cultural Backlash</h4>
                <p className="text-sm text-slate-700 mt-2">Influx of Western movies, music, and values often provokes conservative or nationalist backlash seeking to protect "traditional values" (e.g., Iran or Russia)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Environmental Degradation</h4>
                <p className="text-sm text-slate-700 mt-2">Rapid development funded by global capital often destroys local environments, leading to health crises and citizen alienation (smog in Beijing, oil spills in Niger Delta)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 External Pressures</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Sanctions" 
                definition="Foreign governments utilize economic sanctions to punish regimes for human rights violations or nuclear proliferation (heavy sanctions on Iran and Russia)."
              />
              <KeyTerm 
                term="Treaty Reversals" 
                definition="Changes in international agreements (like Brexit or USMCA renegotiations) force domestic policy pivots."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔥 Domestic Instability</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Civil Society Demands</h4>
                <p className="text-sm text-slate-700 mt-2">Global connectivity helps citizens organize and demand rights (e.g., environmental NGOs in China)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Protests</h4>
                <p className="text-sm text-slate-700 mt-2">Students and disenfranchised groups use social media to coordinate against state</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">State Response</h4>
                <p className="text-sm text-slate-700 mt-2">Authoritarian regimes respond with arrests and social media restrictions (Russia's "sovereign internet" laws, China's "Great Firewall")</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Policies and Economic Liberalization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Neoliberal Economic Policies</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A strategy of economic development calling for free markets, balanced budgets, privatization, free trade, and minimal government intervention in economy.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Specific Actions:</strong> Eliminating subsidies, lowering tariffs, opening economy to FDI</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Measuring Development</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="GDP (Gross Domestic Product)" 
                definition="Total value of goods/services. Measures size."
              />
              <KeyTerm 
                term="GDP Per Capita" 
                definition="GDP divided by population. Measures average income."
              />
              <KeyTerm 
                term="Gini Index (Coefficient)" 
                definition="Measures inequality. (0 = perfect equality, 100 = one person has all wealth). Liberalization often raises Gini score (higher inequality)."
              />
              <KeyTerm 
                term="HDI (Human Development Index)" 
                definition="Measures quality of life (Life expectancy, education, standard of living)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Consequences of Liberalization</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Positive</h4>
                <p className="text-sm text-slate-700 mt-2">✓ Reduced inflation</p>
                <p className="text-sm text-slate-700">✓ Increased national income</p>
                <p className="text-sm text-slate-700">✓ Access to global goods</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Negative</h4>
                <p className="text-sm text-slate-700 mt-2">✗ Inequality: Wealth concentrates in urban areas or among elite</p>
                <p className="text-sm text-slate-700">✗ Social Tension: Removal of subsidies hurts poor (gas price riots in Mexico/Iran)</p>
                <p className="text-sm text-slate-700">✗ Environmental Damage: Urban sprawl and pollution</p>
                <p className="text-sm text-slate-700">✗ Corruption: Privatization often lacks transparency (Russian Oligarchs in 1990s)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: International and Supranational Organizations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Distinction</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">International/Intergovernmental</h4>
                <p className="text-sm text-slate-700 mt-2">Member states cooperate but retain full sovereignty (e.g., UN)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Supranational</h4>
                <p className="text-sm text-slate-700 mt-2">Member states give up some sovereign power. Decisions are binding on member states.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Key Organizations</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">IMF & World Bank</h4>
                <p className="text-sm text-slate-700 mt-2">Provide loans to countries in financial crisis</p>
                <p className="text-sm text-slate-700"><strong>Conditionality:</strong> Loans come with Structural Adjustment Programs (SAPs) requiring privatization, austerity, lower tariffs</p>
                <p className="text-xs text-blue-600 mt-2">Impact: Highly controversial in Nigeria and Mexico; seen as eroding sovereignty and hurting poor</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">WTO (World Trade Organization)</h4>
                <p className="text-sm text-slate-700 mt-2">Sets rules for trade. Pushes for lower tariffs. If country (like China or Russia) joins, they must abide by international trade law, limiting ability to protect domestic industries</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">ECOWAS (Economic Community of West African States)</h4>
                <p className="text-sm text-slate-700 mt-2">Regional bloc including Nigeria. Attempts to create Parliament and single currency. Nigeria often leads peacekeeping missions but bears financial burden</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">EU (European Union)</h4>
                <p className="text-sm text-slate-700 mt-2">Most powerful supranational body</p>
                <p className="text-sm text-slate-700"><strong>UK Context:</strong> UK left EU (Brexit) largely to reclaim sovereignty over border control (immigration) and trade policy, rejecting supranational authority of Brussels</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Import Substitution Industrialization (ISI)</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-slate-700">This is the opposite of liberalization. Policy used by developing nations (historically Mexico and Iran) to protect domestic industries by imposing high tariffs on imports. Encourages citizens to buy locally produced goods. Largely abandoned in favor of neoliberalism.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Adaptation of Social Policies",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Response to Social Changes</h3>
            <p className="text-sm text-slate-700 mb-4">Governments must adapt policies regarding gender, health, and education to maintain legitimacy and stability.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👩 Gender Equity and Rights</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">High literacy rates for women, but banned from certain university fields and sports stadiums. Women vote and sit in Majles, but face legal discrimination in court (testimony worth half a man's) and dress codes (hijab)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Introduced Gender Quotas. Political parties must run certain percentage of female candidates. Resulted in near gender parity in legislature</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Massive North/South divide. Southern women have higher education/literacy; Northern women face barriers due to Sharia law and cultural traditions</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏥 Social Welfare</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>NHS (National Health Service):</strong> Massive state-run system. Austerity measures (budget cuts) due to aging populations strain system, causing political tension</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">Expanding social safety nets (health/pension) to deal with aging population resulting from One Child Policy</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Conditional Cash Transfers (e.g., Oportunidades) - government pays poor families if they keep children in school and get health checkups</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-7",
      title: "Topic 5.7: Impact of Industrialization and Economic Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Environmental Problems</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">Severe air pollution in cities. Government actively moves factories away from cities and heavily subsidizes green technology (EVs, solar) to quell middle-class unrest</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Oil spills in Niger Delta destroyed farming/fishing lands, leading to rise of militant groups (MEND) challenging state</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💼 Trade Liberalization and Labor</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico (NAFTA/USMCA)</h4>
                <p className="text-sm text-slate-700 mt-2">Created Maquiladoras (assembly factories) in North. Created jobs but kept wages low to compete. Led to migration from South to North</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Tariffs</h4>
                <p className="text-sm text-slate-700 mt-2">Lowering tariffs lowers consumer prices but can destroy domestic industries that cannot compete with cheap imports</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💸 Austerity</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">When budget deficits rise (often due to global price fluctuations), governments cut funding to state programs. Almost always sparks protests and challenges political legitimacy (e.g., UK Conservative party policy post-2008)</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-8",
      title: "Topic 5.8: Causes and Effects of Demographic Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Migration Patterns</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">Massive internal migration from Rural to Urban and West to East (interior to coast) for factory jobs. Government tries to manage via Hukou (household registration) system, which restricts social services for migrants</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Migration from South to industrialized North, and external migration to USA (remittances are huge part of economy)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Rural to Urban migration (Lagos exploding in size), creating infrastructure strain and slums</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">Highly skilled workers leaving (Brain Drain) due to political repression and economic stagnation</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👴 Aging Populations</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">One Child Policy created demographic contraction. Too few young workers to support aging elderly</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 UK & 🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2">Low birth rates and aging populations put strain on healthcare and pension systems. Russia actively encourages births (pro-natalist policies)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Immigration Tension</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2">Positive net migration (more coming in) led to social and political tensions, fueling rise of right-wing parties (UKIP) and Brexit vote</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2">Influx of migrants from Central Asian former Soviet republics creates ethnic tension</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-9",
      title: "Topic 5.9: Impact of Natural Resources",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Rentier States</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A country that obtains sizable percentage of total government revenue from export of oil and gas or from leasing the resource to foreign countries (Rent).</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Course Examples:</strong> Iran, Nigeria, Russia</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ The "Resource Curse"</h3>
            <p className="text-sm text-slate-700 mb-4">Countries with abundant natural resources often have less economic growth and less democracy than those without.</p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Lack of Diversification</h4>
                <p className="text-sm text-slate-700 mt-2">Economy relies entirely on one commodity. If oil prices crash, economy crashes (Russia/Nigeria/Iran vulnerable)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Lack of Accountability</h4>
                <p className="text-sm text-slate-700 mt-2">Government gets money from oil, doesn't need to tax citizens. "No taxation means no representation." Government doesn't need people's consent to fund itself</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Corruption</h4>
                <p className="text-sm text-slate-700 mt-2">High stakes control of resources leads to massive theft and patronage (e.g., Nigeria's "prebendalism")</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Inequality</h4>
                <p className="text-sm text-slate-700 mt-2">Wealth concentrated in hands of few who control resource</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Ownership Models</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico:</strong> Historically nationalized (sovereignty pride). Recent trends toward allowing private investment to modernize infrastructure</p>
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> Technically nationalized, but extraction done by foreign MNCs. State acts as gatekeeper for oil wealth</p>
              <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Used natural resources as political tool. State control ensures "commanding heights" of economy serve regime's geopolitical goals</p>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default governmentUnit5Notes;